import type { BaseClient } from '../../http/BaseClient.js';
import type { TokenProvider } from '../../http/tokenProvider.js';
import { openChatStream, type ChatStreamHandle, type OpenChatStreamOptions } from './ChatStream.js';
import type {
  AnswerChatQuestionRequest,
  AnswerChatQuestionResponse,
  CancelChatTurnResponse,
  ChatFeedbackEnvelope,
  ChatScheduleEnvelope,
  ChatScope,
  ConfirmChatToolCallRequest,
  ConfirmChatToolCallResponse,
  CreateChatScheduleRequest,
  CreateChatSessionRequest,
  CreateChatSessionResponse,
  DebugPayload,
  GetChatSessionResponse,
  ListChatFeedbackResponse,
  ListChatSchedulesResponse,
  ListChatScheduleRunsResponse,
  ListChatSessionsQuery,
  ListChatSessionsResponse,
  PostChatMessageRequest,
  PostChatMessageResponse,
  SubmitChatFeedbackRequest,
  UpdateChatScheduleRequest,
} from './types.js';

const BASE_PATH = '/api/agentengine/v1/agent/chat/sessions';

function scopeHeaders(scope: ChatScope): Record<string, string> {
  const sandboxId = scope.sandboxId ?? scope.repoId ?? '';
  const projectId = scope.projectId ?? scope.workspaceId;
  const headers: Record<string, string> = {
    'X-Org-Id': scope.orgId,
    'X-User-Id': scope.userId,
    'X-Sandbox-Id': sandboxId,
    'X-Repo-Id': sandboxId,
  };
  if (projectId) {
    headers['X-Project-Id'] = projectId;
    headers['X-Workspace-Id'] = projectId;
  }
  return headers;
}

class ChatSessionsScope {
  constructor(private readonly http: BaseClient) {}

  create(scope: ChatScope, body: CreateChatSessionRequest): Promise<CreateChatSessionResponse> {
    return this.http.post<CreateChatSessionResponse>(BASE_PATH, {
      body,
      headers: scopeHeaders(scope),
    });
  }

  list(scope: ChatScope, query: ListChatSessionsQuery = {}): Promise<ListChatSessionsResponse> {
    return this.http.get<ListChatSessionsResponse>(BASE_PATH, {
      query,
      headers: scopeHeaders(scope),
    });
  }

  get(scope: ChatScope, conversationId: string): Promise<GetChatSessionResponse> {
    return this.http.get<GetChatSessionResponse>(`${BASE_PATH}/${conversationId}`, {
      headers: scopeHeaders(scope),
    });
  }

  delete(scope: ChatScope, conversationId: string): Promise<void> {
    return this.http.delete<void>(`${BASE_PATH}/${conversationId}`, {
      headers: scopeHeaders(scope),
    });
  }
}

class ChatMessagesScope {
  constructor(private readonly http: BaseClient) {}

  post(scope: ChatScope, conversationId: string, body: PostChatMessageRequest): Promise<PostChatMessageResponse> {
    return this.http.post<PostChatMessageResponse>(`${BASE_PATH}/${conversationId}/messages`, {
      body,
      headers: scopeHeaders(scope),
    });
  }

  /**
   * Interrupts an in-flight chat-loop turn. The engine cancels the turn's
   * Go context and asks the sandbox service to kill any in-flight docker
   * exec inside the per-session container (the container itself survives
   * so the next turn keeps its filesystem state). Returns 404 when no
   * turn matches `turnId` — covers the race where the user clicks Stop
   * just after the turn already finished.
   *
   * The engine still emits a terminal `done` SSE event with
   * status="context_cancelled" after this returns; the frontend should
   * render its "Cancelled" marker on that event rather than on this
   * response, so a reload mid-cancel still ends in the same UI state.
   */
  cancel(scope: ChatScope, conversationId: string, turnId: string): Promise<CancelChatTurnResponse> {
    return this.http.post<CancelChatTurnResponse>(
      `${BASE_PATH}/${conversationId}/messages/${turnId}/cancel`,
      { headers: scopeHeaders(scope) },
    );
  }

  /**
   * Deliver the user's allow/deny decision on an M3 confirmation modal
   * (fired when the policy hook emits a
   * `tool_call_confirmation_required` SSE event). The chat-loop has
   * suspended waiting on this call; on allow it dispatches the tool, on
   * deny it synthesises a "DENIED:" tool result so the model can react.
   * Returns 404 when no pending confirmation exists for the given
   * (turn, tool_call) — usually means the 4-minute confirmer timeout
   * already elapsed.
   */
  confirmToolCall(
    scope: ChatScope,
    conversationId: string,
    turnId: string,
    toolCallId: string,
    body: ConfirmChatToolCallRequest,
  ): Promise<ConfirmChatToolCallResponse> {
    return this.http.post<ConfirmChatToolCallResponse>(
      `${BASE_PATH}/${conversationId}/messages/${turnId}/confirm/${toolCallId}`,
      { body, headers: scopeHeaders(scope) },
    );
  }
}

class ChatQuestionsScope {
  constructor(private readonly http: BaseClient) {}

  answer(
    scope: ChatScope,
    conversationId: string,
    turnId: string,
    body: AnswerChatQuestionRequest,
  ): Promise<AnswerChatQuestionResponse> {
    return this.http.post<AnswerChatQuestionResponse>(
      `${BASE_PATH}/${conversationId}/messages/${turnId}/answers`,
      { body, headers: scopeHeaders(scope) },
    );
  }
}

/**
 * Debug scope — fetches the consolidated payload (session, messages) used by
 * the dashboard Debug tab. Keyed by conversation ID.
 */
class ChatDebugScope {
  constructor(private readonly http: BaseClient) {}

  getByConversation(scope: ChatScope, conversationId: string): Promise<DebugPayload> {
    return this.http.get<DebugPayload>(`${BASE_PATH}/${conversationId}/debug`, {
      headers: scopeHeaders(scope),
    });
  }
}

/**
 * Schedules scope — manages the M3 Stage 4 cron-driven prompt
 * schedules. Repo-scoped: list / create / get / update / delete are
 * keyed by (repo, schedule_id). The dispatcher fires due schedules
 * server-side on its tick; the SDK only handles the CRUD surface.
 *
 * Endpoint shape:
 *   GET    /v1/agent/repos/:repo_id/schedules
 *   POST   /v1/agent/repos/:repo_id/schedules
 *   GET    /v1/agent/repos/:repo_id/schedules/:id
 *   PUT    /v1/agent/repos/:repo_id/schedules/:id
 *   DELETE /v1/agent/repos/:repo_id/schedules/:id
 */
class ChatSchedulesScope {
  constructor(private readonly http: BaseClient) {}

  private repoPath(scope: ChatScope): string {
    const repoID = scope.sandboxId ?? scope.repoId ?? '';
    return `/api/agentengine/v1/agent/repos/${repoID}/schedules`;
  }

  list(scope: ChatScope): Promise<ListChatSchedulesResponse> {
    return this.http.get<ListChatSchedulesResponse>(this.repoPath(scope), {
      headers: scopeHeaders(scope),
    });
  }

  create(scope: ChatScope, body: CreateChatScheduleRequest): Promise<ChatScheduleEnvelope> {
    return this.http.post<ChatScheduleEnvelope>(this.repoPath(scope), {
      body,
      headers: scopeHeaders(scope),
    });
  }

  get(scope: ChatScope, scheduleId: string): Promise<ChatScheduleEnvelope> {
    return this.http.get<ChatScheduleEnvelope>(`${this.repoPath(scope)}/${scheduleId}`, {
      headers: scopeHeaders(scope),
    });
  }

  update(
    scope: ChatScope,
    scheduleId: string,
    body: UpdateChatScheduleRequest,
  ): Promise<ChatScheduleEnvelope> {
    return this.http.put<ChatScheduleEnvelope>(`${this.repoPath(scope)}/${scheduleId}`, {
      body,
      headers: scopeHeaders(scope),
    });
  }

  delete(scope: ChatScope, scheduleId: string): Promise<{ status: 'deleted' }> {
    return this.http.delete<{ status: 'deleted' }>(`${this.repoPath(scope)}/${scheduleId}`, {
      headers: scopeHeaders(scope),
    });
  }

  /**
   * List recent chat sessions spawned by a schedule's cron tick — used
   * by the SchedulesTab "Runs" panel to surface real audit trail
   * instead of dumping the user into the global chat list.
   *
   * Each entry is a full ChatSession row; `.id` is the conversation id
   * the dashboard navigates to (`/chat/:conversationId`). All rows are
   * unattended (the scheduler is the only writer of `schedule_id`),
   * which makes the chat session viewer render the "Scheduled run"
   * ribbon and hide the composer.
   *
   * Server returns the 50 most-recent rows; no cursor pagination in
   * v1 (deferred until the long-tail audit case proves real).
   */
  listRuns(scope: ChatScope, scheduleId: string): Promise<ListChatScheduleRunsResponse> {
    return this.http.get<ListChatScheduleRunsResponse>(
      `${this.repoPath(scope)}/${scheduleId}/runs`,
      { headers: scopeHeaders(scope) },
    );
  }
}

/**
 * Feedback scope — a user's "Satisfied? Yes/No" rating + optional comment on a
 * chat session, and the list of feedback recorded for it (so the UI can
 * pre-fill and so an org admin can review past ratings). Conversation-scoped;
 * pass `turn_id` in the body to scope feedback to a single turn.
 *
 * Endpoint shape:
 *   POST /v1/agent/chat/sessions/:id/feedback
 *   GET  /v1/agent/chat/sessions/:id/feedback
 */
class ChatFeedbackScope {
  constructor(private readonly http: BaseClient) {}

  submit(
    scope: ChatScope,
    conversationId: string,
    body: SubmitChatFeedbackRequest,
  ): Promise<ChatFeedbackEnvelope> {
    return this.http.post<ChatFeedbackEnvelope>(`${BASE_PATH}/${conversationId}/feedback`, {
      body,
      headers: scopeHeaders(scope),
    });
  }

  list(scope: ChatScope, conversationId: string): Promise<ListChatFeedbackResponse> {
    return this.http.get<ListChatFeedbackResponse>(`${BASE_PATH}/${conversationId}/feedback`, {
      headers: scopeHeaders(scope),
    });
  }
}

export class ChatClient {
  readonly sessions: ChatSessionsScope;
  readonly messages: ChatMessagesScope;
  readonly questions: ChatQuestionsScope;
  readonly debug: ChatDebugScope;
  readonly schedules: ChatSchedulesScope;
  readonly feedback: ChatFeedbackScope;

  constructor(
    private readonly http: BaseClient,
    private readonly streamDeps: { baseUrl: string; getToken?: TokenProvider },
  ) {
    this.sessions = new ChatSessionsScope(http);
    this.messages = new ChatMessagesScope(http);
    this.questions = new ChatQuestionsScope(http);
    this.debug = new ChatDebugScope(http);
    this.schedules = new ChatSchedulesScope(http);
    this.feedback = new ChatFeedbackScope(http);
  }

  openStream(options: OpenChatStreamOptions): Promise<ChatStreamHandle> {
    return openChatStream(this.streamDeps, options);
  }
}
